'use client'

import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Bell, UserPlus } from 'lucide-react'

import { UserModal } from '@/features/profiles'
import type { FeedItem as FeedItemType } from '@/features/profiles/types/feed'
import {
  isFollowFeedItem,
  isNotificationFeedItem,
  isProgressFeedItem
} from '@/features/profiles/types/feed'
import { SocialFeedActionBlock } from '@/features/social/components/SocialFeedActionBlock'
import { useModalContext } from '@/shared/contexts/Modal'
import { useIsCurrentViewer } from '@/shared/hooks/useCurrentUserUid'
import { useUsersAnalytics } from '@/shared/lib/analytics'
import { Badge } from '@/shared/ui/badge'
import { CosmeticAvatarComposite } from '@/shared/ui/CosmeticAvatarComposite'
import {
  FeedActivityAccentWell,
  FeedCardSurface
} from '@/shared/ui/FeedCardSurface'

function getNamedResourceTitle(resource: unknown) {
  if (typeof resource !== 'object' || resource === null) return null
  if (!('name' in resource)) return null
  const name = (resource as { name?: unknown }).name
  return typeof name === 'string' ? name : null
}

interface SocialFeedActivityCardProps {
  item: FeedItemType
  style?: React.CSSProperties
}

function RelationshipBadge({
  itsMe,
  isFollowing
}: {
  itsMe: boolean
  isFollowing: boolean
}) {
  if (itsMe) return null
  if (isFollowing) {
    return (
      <Badge className="border-0 bg-white/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-white/90 sm:text-[10px]">
        Seguindo
      </Badge>
    )
  }
  return (
    <Badge className="border-0 bg-primary-dark px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-white shadow-[0_1px_0_0_rgba(0,0,0,0.35)] sm:text-[10px]">
      Seguir
    </Badge>
  )
}

export function SocialFeedActivityCard({
  item,
  style
}: SocialFeedActivityCardProps) {
  const { openModal } = useModalContext()
  const itsMe = useIsCurrentViewer(item.from.uid)
  const { trackUserFeedInteraction } = useUsersAnalytics()
  const actorAvatar = item.from.avatar
  const actorFrame = item.from.frame
  const actorBackground = item.from.background

  const openActorProfile = () => {
    trackUserFeedInteraction('clicked', item.from.uid)
    openModal({ modal: UserModal }, { userUid: item.from.uid })
  }

  if (isNotificationFeedItem(item)) {
    const resource = item.resource
    const imageUrl = resource.image?.file?.url
    const linkUrl = resource.link_url || undefined
    const linkTitle = resource.link_title || 'Abrir link'

    return (
      <article className="social-feed-card-enter" style={style}>
        <FeedCardSurface>
          <div className="flex gap-2 sm:gap-2.5">
            <button
              type="button"
              onClick={openActorProfile}
              className="shrink-0 rounded-full outline-none ring-offset-2 ring-offset-[#252538] transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-jacaranda"
              aria-label={
                itsMe ? 'Abrir seu perfil' : `Perfil de ${item.from.name}`
              }
            >
              <CosmeticAvatarComposite
                avatar={actorAvatar}
                frame={actorFrame}
                background={actorBackground}
                size="sm"
                className="shrink-0 ring-1 ring-inset ring-white/20"
              />
            </button>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight tracking-tight text-white">
                    {itsMe ? 'Você' : item.from.name}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-[#7F7FAD]">
                    <span>
                      {formatDistanceToNow(new Date(item.created_at), {
                        addSuffix: true,
                        locale: ptBR
                      })}
                    </span>
                    <span className="text-white/20" aria-hidden>
                      ·
                    </span>
                    <span className="font-medium text-[#be64e3]">Aviso</span>
                  </p>
                </div>
                <FeedActivityAccentWell>
                  <Bell
                    className="size-5 text-[#fbe576] drop-shadow-[0_0_10px_rgba(251,229,118,0.32)] sm:size-[1.35rem]"
                    strokeWidth={2}
                    aria-hidden
                  />
                </FeedActivityAccentWell>
              </div>
              <div className="flex flex-col gap-1.5 pt-2 sm:gap-2 sm:pt-2.5">
                <h2 className="text-[13px] font-semibold leading-snug text-white sm:text-sm">
                  {resource.subject}
                </h2>
                {resource.subtitle ? (
                  <p className="text-xs font-medium text-primary-foreground sm:text-[13px]">
                    {resource.subtitle}
                  </p>
                ) : null}
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="max-h-40 w-full rounded-lg object-cover ring-1 ring-inset ring-white/10 sm:max-h-48 sm:rounded-xl"
                  />
                ) : null}
                <p className="text-xs leading-relaxed text-[#c8c8dc] whitespace-pre-wrap sm:text-[13px] sm:leading-snug">
                  {resource.content}
                </p>
                {linkUrl ? (
                  <a
                    href={linkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="w-fit text-xs font-semibold text-primary-foreground underline decoration-primary-foreground/40 underline-offset-2 transition-colors hover:decoration-primary-foreground sm:text-[13px]"
                  >
                    {linkTitle}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
          <SocialFeedActionBlock item={item} />
        </FeedCardSurface>
      </article>
    )
  }

  const resourceType = isProgressFeedItem(item)
    ? item.resource.resource_type
    : null
  const isLevel = resourceType === 'Level'
  const isMission = resourceType === 'Mission'
  const isFollow = isFollowFeedItem(item)

  const title =
    isProgressFeedItem(item) && (isLevel || isMission)
      ? (getNamedResourceTitle(item.resource.resource) ?? '')
      : ''

  const accent = isLevel ? (
    <img
      src="/icons/throphy.svg"
      alt=""
      className="size-7 drop-shadow-[0_2px_5px_rgba(0,0,0,0.42)] sm:size-8"
    />
  ) : isMission ? (
    <img
      src="/icons/missions.svg"
      alt=""
      className="size-6 opacity-95 drop-shadow-[0_2px_5px_rgba(0,0,0,0.42)] sm:size-7"
    />
  ) : isFollow ? (
    <UserPlus
      className="size-6 text-[#9ff0bd] sm:size-[1.65rem]"
      strokeWidth={2.25}
      aria-hidden
    />
  ) : null

  return (
    <article className="social-feed-card-enter" style={style}>
      <FeedCardSurface>
        <div className="flex gap-2 sm:gap-2.5">
          <button
            type="button"
            onClick={openActorProfile}
            className="shrink-0 rounded-full outline-none ring-offset-2 ring-offset-[#252538] transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-jacaranda"
            aria-label={
              itsMe ? 'Abrir seu perfil' : `Perfil de ${item.from.name}`
            }
          >
            <CosmeticAvatarComposite
              avatar={actorAvatar}
              frame={actorFrame}
              background={actorBackground}
              size="sm"
              className="shrink-0 ring-1 ring-inset ring-white/20"
            />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold leading-tight tracking-tight text-white">
                  {itsMe ? 'Você' : item.from.name}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <time
                    className="text-[11px] font-medium text-[#7F7FAD]"
                    dateTime={item.created_at}
                  >
                    {formatDistanceToNow(new Date(item.created_at), {
                      addSuffix: true,
                      locale: ptBR
                    })}
                  </time>
                  <RelationshipBadge
                    itsMe={itsMe}
                    isFollowing={item.from.is_following}
                  />
                </div>
              </div>
              {accent ? (
                <FeedActivityAccentWell>{accent}</FeedActivityAccentWell>
              ) : null}
            </div>

            <div className="pt-1.5 sm:pt-2">
              {isLevel && (
                <p className="leading-snug text-[#c8c8dc] sm:text-[0.9375rem] md:text-[0.875rem]">
                  <span className="font-medium text-white/90">
                    Concluiu o nível
                  </span>{' '}
                  <span className="font-bold text-primary-foreground">
                    {title}
                  </span>
                </p>
              )}
              {isMission && (
                <p className="leading-snug text-[#c8c8dc] sm:text-[0.9375rem] md:text-[0.875rem]">
                  <span className="font-medium text-white/90">
                    Concluiu a missão
                  </span>{' '}
                  <span className="font-bold text-primary-foreground">
                    {title}
                  </span>
                </p>
              )}
              {isFollow && !isLevel && !isMission && (
                <p className="text-[13px] font-semibold leading-snug text-white sm:text-[0.9375rem]">
                  Começou a seguir você
                </p>
              )}
            </div>
          </div>
        </div>
        <SocialFeedActionBlock item={item} />
      </FeedCardSurface>
    </article>
  )
}
